import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ParticipantDto } from '@api/projects/interfaces';
import { ActivatableExportConfig } from '@app/components/elements/exports/select-exports-button.component';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { participantPages } from '@app/pages/participant/participants.routes';
import { MenuService } from '@app/services/menu';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { TParticipantSearchResult } from '@app/services/participants/interfaces';
import { PermissionService } from '@app/services/permission/permission.service';
import { ProjectService } from '@app/services/project/project.service';
import { ParticipantRestService } from '@app/services/rest/participant-rest.service';
import { WorkflowItemNavigationService } from '@workflow/services/workflow-item-navigation.service';
import { WorkflowService } from '@workflow/services/workflow.service';
import { maxBy } from 'lodash';
import { NzTablePaginationPosition, NzTableSize, NzTableSortFn } from 'ng-zorro-antd/table/src/table.types';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map, startWith, switchMap, first } from 'rxjs/operators';
import { createColumns, WfsColumn } from '@app/pages/participant/list/wfs-columns';
import { firstValueFrom } from '@app/util/rxjs-7-compat';
import { SelectionModel } from '@angular/cdk/collections';
import { isNotNil } from 'ng-zorro-antd/core/util';

interface TableProps {
	header: boolean;
	footer: boolean;
	bordered: boolean;
	position: NzTablePaginationPosition;
	sizeChanger: boolean;
	simple: boolean;
	size: NzTableSize;
	pagination: boolean;
	pageSize: number;
}

export interface TableViewState {
	// Table setup
	props: TableProps,
	columns: WfsColumn[],
	// Table Data
	participants: ParticipantDto[],
	newestParticipantId: string;
	selectionState: TableSelectionState;
}

interface ExportButtonViewState {
	selected: Set<string>;
	configurations: ActivatableExportConfig[];
	participantCount: number;
}

type TableSelectionState = 'none' | 'some' | 'all';

const LATEST_PARTICIPANT_TIMEOUT_MS = 5*60*1000; // 5 Minuten

function getNewestParticipant(participantList: ParticipantDto[]): string | null {
	const newest = maxBy(participantList, data => data.creationTimestamp);
	
	if (newest && (LATEST_PARTICIPANT_TIMEOUT_MS === 0 || newest.creationTimestamp >= Date.now() - LATEST_PARTICIPANT_TIMEOUT_MS)) {
		return newest.id;
	} else {
		return null;
	}
}

@Component({
	selector: 'app-participant-workflow-list',
	templateUrl: './participant-workflow-list.component.html',
	styleUrls: ['./participant-workflow-list.component.scss']
})

export class ParticipantWorkflowListComponent implements OnDestroy {
	private readonly subscription = new Subscription();

	// Outputs von der AppSearchComponent
	public readonly searchResult$ = new BehaviorSubject<TParticipantSearchResult | null>(null);
	
	public readonly pseudonymFilter$ = new BehaviorSubject<string>('');
	
	public readonly showParticipantAdd$: Observable<boolean>;
	public readonly hasProject$: Observable<boolean>;
	
	private readonly privacyFilteredParticipants$ = new BehaviorSubject<ParticipantDto[] | null>(null);
	
	public readonly participantSelectionModel = new SelectionModel<string>(true, null);
	public readonly checkedParticipants$: Observable<ParticipantDto[]>;	 
	
	public readonly privacyMode$: Observable<boolean>;
	
	public participantAddSplitButton: SplitButtonElement = {
		title: 'participants.add',
		icon: 'plus',
		type: 'default',
		callback: () => this.showParticipantCreate(),
	};
	
	private readonly tableProps$ = new BehaviorSubject<TableProps>({
		header: true,
		footer: false,
		bordered: false,
		position: 'bottom',
		sizeChanger: false,
		simple: false,
		size: 'small',
		pagination: true,
		pageSize: 25,
	});
	public readonly exportButtonViewState$: Observable<ExportButtonViewState>; 
	public readonly tableViewState$: Observable<TableViewState | null>;
	
	public readonly comparePseudonym: NzTableSortFn = (a: ParticipantDto, b: ParticipantDto) => {
		return a.pseudonym?.localeCompare(b.pseudonym);
	};
	
	constructor(
			private router: Router,
			private permissionService: PermissionService,
			private participantRestService: ParticipantRestService,
			private activeParticipantService: ActiveParticipantService,
			private workflowService: WorkflowService,
			private workflowItemService: WorkflowItemNavigationService,
			private projectService: ProjectService,
			private menuService: MenuService,
	) {
		this.privacyMode$ = of(false); // Ersetzen durch externen Service, wenn angebracht.
		this.hasProject$ = projectService.activeProject$.pipe(map(activeProject => !!activeProject));
		
		const canParticipantCreate$ = permissionService.hasProjectPermission$('participants:create');
		
		this.redirectToOldList();
		
		const participants$ = this.projectService.activeProject$.pipe(
			switchMap(activeProject => {
				if (!activeProject) {
					return of([]);
				} else {
					return this.participantRestService.listParticipants$(activeProject.project_id, true);
				}
			})
		);
		
		// Hack um nach dem Laden der Seite Daten zu haben.
		this.subscription.add(
			participants$.pipe(
				first()
			).subscribe(ps => this.searchResult$.next({
				results: ps,
				status: 'valid'
			}))
		);
		
		this.checkedParticipants$ = combineLatest([ 
			participants$, 
			this.participantSelectionModel.changed.pipe(startWith({source: this.participantSelectionModel}))
		]).pipe(
			map(([participants, {source: model}]) => participants.filter(p => model.isSelected(p.id)))
		);
		
		const filteredParticipants$: Observable<ParticipantDto[] | null> = combineLatest([
			participants$,
			this.searchResult$,
			this.pseudonymFilter$.pipe(map(pseudonym => pseudonym.toLocaleLowerCase()))
		]).pipe(
			map(([participants, filter, pseudonym]) => {
				if (filter === null){
					return null;
				} else {
					const ids = new Set(filter.results.map(r => r.id));
					
					let result = participants.filter(
						p => ids.has(p.id)
					);
					
					if (pseudonym.length > 0){
						result = result.filter(
							item => item.pseudonym?.toLocaleLowerCase().includes(pseudonym)
						);
					}
					
					return result;
				}
			})
		);
		
		this.subscription.add(
			this.privacyMode$.pipe(
				switchMap((allowOnlyExactMatches) => {
					if (allowOnlyExactMatches){
						return filteredParticipants$.pipe(
							map(res => {
								if (res === null) {
									return null;
								} else {
									return res.length === 1 ? res : [];
								}
							})
						);
					} else {
						return filteredParticipants$;
					}
				})
			).subscribe(res => this.privacyFilteredParticipants$.next(res))
		);
		
		const columns$ = this.workflowService.workflow$.pipe(
			filter(isNotNil),
			map(createColumns)
		);
		
		const selectionState$ = combineLatest([
			this.privacyFilteredParticipants$,
			this.participantSelectionModel.changed.pipe(startWith({source: this.participantSelectionModel}))
		]).pipe(
			map(([participants, {source: model}])=>{
				if (participants?.every(p => model.isSelected(p.id))){
					return 'all';
				} else if (!model.isEmpty()){
					return 'some';
				} else {
					return 'none';
				}
			})
		);
		
		this.tableViewState$ = combineLatest([
			this.tableProps$,
			this.privacyFilteredParticipants$,
			columns$,
			selectionState$,
		]).pipe(
			map(([tableProps, participants, cols, selectionState]) =>
				participants ? ({
					props: tableProps,
					participants,
					columns: cols,
					newestParticipantId: getNewestParticipant(participants),
					selectionState,
				}) : null)
		);
		
		const participantCount$ = this.privacyFilteredParticipants$.pipe(
			map(res => res?.length ?? -1)
		);
		
		const configs$ = this.projectService.exportsConfig$.pipe(
			map(configs => configs?.filter(c => c?.showInParticipantWorkflowOverview).map(config => ({...config, active: true}))),
		);
		
		this.exportButtonViewState$ = combineLatest([
			configs$,
			participantCount$,
			this.participantSelectionModel.changed
		]).pipe(map(([config, participantCount, { source }])=>({
			selected: new Set<string>(source.selected),
			configurations: config,
			participantCount
		})));
		
		this.showParticipantAdd$ = combineLatest([this.hasProject$, canParticipantCreate$]).pipe(
			map(([hasProject, hasPermission]) => hasProject && hasPermission),
		);
		
		this.menuService.title = 'person.participants';
	}
	
	/**
	 * @deprecated Bitte im Router umsetzen TODO: GSP-1070
	 * @private
	 */
	private redirectToOldList() {
		const canParticipantRead$ = this.permissionService.hasProjectPermission$('participants:read');
		const canWorkflowRead$ = this.permissionService.hasProjectPermission$('workflow:item:read');
		
		const isParticipantWorkflowList$ = this.projectService.participantsConfig$.pipe(
			map(config => config?.details?.usedParticipantList === 'verlaufsdiagramm'),
		);
		
		const switchToParticipantList$ = combineLatest([
			isParticipantWorkflowList$,
			canParticipantRead$,
			canWorkflowRead$]).pipe(
			map((([isShowWorkflowList, canParticipantRead, canWorkflowRead]) => (!isShowWorkflowList || !canWorkflowRead) && canParticipantRead)),
		);
		
		this.subscription.add(switchToParticipantList$.subscribe(state => {
			if (state) {
				this.router.navigate([participantPages.dashboard.base()]).catch(console.error);
			}
		}));
	}
	
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
	
	// Project Wechsel auf WFS-Teilnehmerseite -> originale Teilnehmerliste (wie bfs)
	public async navigateToWorkflowDetail(participant: ParticipantDto, id: string): Promise<void> {
		const workflow = await firstValueFrom(this.workflowService.workflow$);
		const activeItem = workflow.items.find(item => item.id === id);
		
		this.activeParticipantService.activeParticipant = participant;
		await this.router
			.navigate([this.workflowItemService.getFirstAvailableEntryURL(activeItem)], {queryParams:{continue: true}})
			.catch(console.error);
	}

	showParticipantProfil(participant: ParticipantDto): void {
		this.activeParticipantService.activeParticipant = participant;
		this.router
			.navigate([participantPages.stammDaten.base()])
			.catch(console.error);
	}

	showParticipantCreate(): void {
		this.router
			.navigate([participantPages.create.base()])
			.catch(console.error);
	}

	async onAllChecked(event: boolean): Promise<void> {
		if (!event) {
			this.participantSelectionModel.clear();
		} else {
			const participants = (await firstValueFrom(this.privacyFilteredParticipants$)) ?? [];
			this.participantSelectionModel.select(...participants.map(p => p.id));
		}
	}
}
